import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Truck, Container, Disc3, AlertTriangle, PackageCheck, Wallet, ClipboardCheck, CheckCircle2, Wrench, AlertOctagon, PowerOff } from "lucide-react";
import { STATUS_META } from "../components/StatusBadge";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const CARD_BASE =
  "bg-white border-2 border-slate-200 hover:border-slate-900 p-5 rounded-sm transition-colors";

function Metric({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div className={CARD_BASE} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
          <div className="font-display text-4xl font-black text-slate-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1 font-mono">{sub}</div>}
        </div>
        <div className={`w-10 h-10 flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  const loadSummary = () => api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { loadSummary(); }, []);
  useAutoRefresh(loadSummary, 30000);

  const isMgmt = user?.role === "management" || user?.role === "admin";

  return (
    <div>
      {/* Company banner */}
      <div className="mb-6 border-2 border-slate-900 bg-white rounded-sm p-5 flex items-center gap-5 shadow-sm">
        <img src="/logo-rpi.jpg" alt="PT Royal Persada Indah" className="w-20 h-20 object-contain flex-shrink-0" data-testid="dashboard-logo" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Perseroan Terbatas</div>
          <div className="font-display font-black text-slate-900 text-2xl leading-tight" data-testid="company-name">
            Royal Persada Indah
          </div>
          <div className="text-xs text-slate-600 mt-1">RPI Fleet Monitor · Sistem Monitoring Armada</div>
        </div>
        <div className="hidden md:block text-right">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Selamat datang</div>
          <div className="font-display font-black text-slate-900" data-testid="welcome-user">{user?.name}</div>
        </div>
      </div>

      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Status armada real-time, biaya operasional & manajemen ban.</p>
        </div>
      </div>

      {/* Fleet Status */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-black text-slate-900">Status Armada</h2>
          <Link to="/logbook" className="text-xs font-bold uppercase tracking-wider text-orange-600 hover:text-orange-700 flex items-center gap-1" data-testid="link-logbook">
            <ClipboardCheck className="w-4 h-4" /> Buka Logbook →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatusMetric statusKey="siap_jalan" value={summary?.fleet_status?.siap_jalan ?? 0} testId="fleet-siap-jalan" />
          <StatusMetric statusKey="maintenance" value={summary?.fleet_status?.maintenance ?? 0} testId="fleet-maintenance" />
          <StatusMetric statusKey="rusak" value={summary?.fleet_status?.rusak ?? 0} testId="fleet-rusak" />
          <StatusMetric statusKey="non_aktif" value={summary?.fleet_status?.non_aktif ?? 0} testId="fleet-non-aktif" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          label="Head Truck"
          value={summary?.trucks ?? "-"}
          icon={Truck}
          accent="bg-slate-900 text-orange-400"
          testId="metric-trucks"
        />
        <Metric
          label="Trailer"
          value={summary?.trailers ?? "-"}
          icon={Container}
          accent="bg-slate-900 text-orange-400"
          testId="metric-trailers"
        />
        <Metric
          label="Ban Terpasang"
          value={summary?.tyres_terpasang ?? "-"}
          sub={`${summary?.tyres_total ?? 0} ban total`}
          icon={Disc3}
          accent="bg-emerald-100 text-emerald-700"
          testId="metric-tyres-installed"
        />
        <Metric
          label="Ban Stok"
          value={summary?.tyres_stok ?? "-"}
          sub={`${summary?.tyres_rusak ?? 0} rusak`}
          icon={PackageCheck}
          accent="bg-orange-100 text-orange-700"
          testId="metric-tyres-stock"
        />
      </div>

      {isMgmt && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className={CARD_BASE} data-testid="metric-monthly-cost">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Biaya Ban Bulan Ini</div>
                <div className="font-display text-3xl font-black text-slate-900 mt-2">
                  {formatIDR(summary?.monthly_cost || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">Total pembelian ban bulan berjalan</div>
              </div>
              <div className="w-12 h-12 bg-orange-500 flex items-center justify-center text-slate-900">
                <Wallet className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className={CARD_BASE} data-testid="metric-inventory-value">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Nilai Inventori Ban</div>
                <div className="font-display text-3xl font-black text-slate-900 mt-2">
                  {formatIDR(summary?.total_inventory_value || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">Total nilai pembelian semua ban</div>
              </div>
              <div className="w-12 h-12 bg-slate-900 flex items-center justify-center text-orange-400">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Akses Cepat</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickLink to="/trucks" label="Kelola Truck" icon={Truck} />
          <QuickLink to="/trailers" label="Kelola Trailer" icon={Container} />
          <QuickLink to="/tyres" label="Kelola Ban" icon={Disc3} />
          {isMgmt && <QuickLink to="/reports/cost" label="Laporan Biaya" icon={Wallet} />}
        </div>
      </div>
    </div>
  );
}

function StatusMetric({ statusKey, value, testId }) {
  const meta = STATUS_META[statusKey];
  const Icon = meta.icon;
  return (
    <div className="bg-white border-2 border-slate-200 hover:border-slate-900 p-5 rounded-sm transition-colors" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{meta.label}</div>
          <div className="font-display text-4xl font-black text-slate-900 mt-2">{value}</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">unit</div>
        </div>
        <div className={`w-10 h-10 flex items-center justify-center border-2 ${meta.cls}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, label, icon: Icon }) {
  return (
    <Link
      to={to}
      className="block border-2 border-slate-900 bg-white p-4 rounded-sm hover:bg-slate-900 hover:text-white group transition-colors btn-industrial"
      data-testid={`quick-${to.replace(/\//g, "-")}`}
    >
      <Icon className="w-6 h-6 text-slate-900 group-hover:text-orange-400 mb-2" />
      <div className="font-bold uppercase text-sm tracking-wider">{label}</div>
    </Link>
  );
}
